/**
 * Client for the resumable scan-session backend (TNK-194). A scan session is
 * an import draft with `kind='scan'`; these wrappers create it, append items as
 * uploads land, persist identify results + edits, list an open session for
 * resume, rehydrate it, and discard it. See docs/design/scan-intake.md.
 */
import { apiFetch } from "@/lib/apiClient";

export interface ScanDraftItem {
  id: string;
  imageFrontRef?: string;
  imageBackRef?: string;
  parsed: Record<string, unknown>;
  enrichmentStatus: string;
}

export interface ScanDraft {
  id: string;
  kind: string;
  status: string;
  totalRows: number;
}

export interface NewScanItem {
  imageFrontRef: string;
  imageBackRef?: string;
  parsed?: Record<string, unknown>;
}

async function jsonBody(path: string, method: string, body: unknown) {
  const res = await apiFetch(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`${method} ${path} → ${res.status}`);
  return res.json();
}

export async function createScanDraft(
  items: NewScanItem[],
): Promise<{ draft: ScanDraft; items: ScanDraftItem[] }> {
  return jsonBody("/api/imports/scan", "POST", { items });
}

export async function appendScanItems(
  draftId: string,
  items: NewScanItem[],
): Promise<{ items: ScanDraftItem[] }> {
  return jsonBody(`/api/imports/${draftId}/scan-items`, "POST", { items });
}

/** The operator's authoritative final state for one card, committed to the
 *  server in the identify call so the worker reads the corrected side. The
 *  server only accepts a front/back that already belongs to the item. */
export interface ScanFinalizeItem {
  itemId: string;
  imageFrontRef: string;
  imageBackRef: string | null;
  condition?: string;
}

/** Kick server-side background identification of a scan draft (TNK-218).
 *  `finalize` commits the operator's authoritative orientation + condition in
 *  the same call, atomically, BEFORE the worker runs — so identification can't
 *  race a pending flip. Throws on a non-2xx (the caller surfaces it and keeps
 *  the operator on the upload stage). Returns the count the server finalized.
 *  Then poll `getScanDraft` for progress (draft.status → 'ready'). */
export async function startScanIdentify(
  draftId: string,
  finalize: ScanFinalizeItem[] = [],
): Promise<{ finalized: number }> {
  const res = await apiFetch(`/api/imports/${draftId}/identify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ finalize }),
  });
  if (!res.ok) throw new Error(`start identify → ${res.status}`);
  return res.json();
}

export async function patchScanItem(
  draftId: string,
  itemId: string,
  parsed: Record<string, unknown>,
): Promise<void> {
  await jsonBody(`/api/imports/${draftId}/items/${itemId}`, "PATCH", {
    parsed,
  });
}

/** Swap front/back image refs on one item (persists a per-card flip). */
export async function swapScanItemImages(
  draftId: string,
  itemId: string,
): Promise<void> {
  const res = await apiFetch(
    `/api/imports/${draftId}/items/${itemId}/swap-images`,
    { method: "POST" },
  );
  if (!res.ok) throw new Error(`swap-images → ${res.status}`);
}

/** Swap front/back on every item in the session (persists Flip all). */
export async function swapAllScanItemImages(draftId: string): Promise<void> {
  const res = await apiFetch(`/api/imports/${draftId}/swap-images-all`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`swap-images-all → ${res.status}`);
}

export async function deleteScanItem(
  draftId: string,
  itemId: string,
): Promise<void> {
  const res = await apiFetch(`/api/imports/${draftId}/items/${itemId}`, {
    method: "DELETE",
  });
  if (!res.ok && res.status !== 404)
    throw new Error(`DELETE item → ${res.status}`);
}

export async function deleteScanDraft(draftId: string): Promise<void> {
  const res = await apiFetch(`/api/imports/${draftId}`, { method: "DELETE" });
  if (!res.ok && res.status !== 404)
    throw new Error(`DELETE draft → ${res.status}`);
}

/** Open (uncommitted) scan sessions for this store, newest first. */
export async function listOpenScanDrafts(): Promise<ScanDraft[]> {
  const res = await apiFetch("/api/imports?kind=scan");
  if (!res.ok) return [];
  return res.json();
}

export async function getScanDraft(
  draftId: string,
): Promise<{ draft: ScanDraft; items: ScanDraftItem[] }> {
  const res = await apiFetch(`/api/imports/${draftId}`);
  if (!res.ok) throw new Error(`GET draft → ${res.status}`);
  return res.json();
}

/** Commit the session into inventory (shared confirm path; the server applies
 *  the condition-aware merge for kind='scan'). Returns how many cards landed. */
export async function confirmScanDraft(
  draftId: string,
  binId: string | null,
): Promise<{ cards: number; cardIds: string[] }> {
  return jsonBody(`/api/imports/${draftId}/confirm`, "POST", { binId });
}
